import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, MessageCircle, ArrowRight, Search, Trophy, Heart, Wallet, Wrench, Compass, Sparkles } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, {
  PageHero, LightSection, SectionHead, ClosingBanner, PrimaryCTA, SecondaryCTA,
} from '@/components/PageShell';
import { cn } from '@/lib/utils';

const FAQ_DESCRIPTION =
  "Frequently asked questions about Sport Fund — how to raise support, how to give, fees, payouts and more. For athletes and supporters.";

type CategoryKey = 'general' | 'forCreators' | 'forSupporters' | 'fees' | 'support' | 'comingSoon';

interface FAQItem {
  category: CategoryKey;
  qKey: string;
}

const CATEGORY_META: Record<CategoryKey, { icon: typeof Trophy; tone: string }> = {
  general: { icon: Compass, tone: 'bg-blue-50 text-blue-700 border-blue-100' },
  forCreators: { icon: Trophy, tone: 'bg-yellow-50 text-yellow-700 border-yellow-100' },
  forSupporters: { icon: Heart, tone: 'bg-rose-50 text-rose-700 border-rose-100' },
  fees: { icon: Wallet, tone: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  support: { icon: Wrench, tone: 'bg-purple-50 text-purple-700 border-purple-100' },
  comingSoon: { icon: Sparkles, tone: 'bg-amber-50 text-amber-700 border-amber-100' },
};

const FAQ_INDEX: FAQItem[] = [
  { category: 'general', qKey: 'q1' }, { category: 'general', qKey: 'q2' },
  { category: 'general', qKey: 'q3' }, { category: 'general', qKey: 'q4' },
  { category: 'forCreators', qKey: 'q5' }, { category: 'forCreators', qKey: 'q6' },
  { category: 'forCreators', qKey: 'q7' }, { category: 'forCreators', qKey: 'q8' },
  { category: 'forCreators', qKey: 'q9' }, { category: 'forCreators', qKey: 'q10' },
  { category: 'forCreators', qKey: 'q11' }, { category: 'forCreators', qKey: 'q12' },
  { category: 'forSupporters', qKey: 'q13' }, { category: 'forSupporters', qKey: 'q14' },
  { category: 'forSupporters', qKey: 'q15' }, { category: 'forSupporters', qKey: 'q16' },
  { category: 'fees', qKey: 'q17' }, { category: 'fees', qKey: 'q18' }, { category: 'fees', qKey: 'q19' },
  { category: 'support', qKey: 'q20' }, { category: 'support', qKey: 'q21' }, { category: 'support', qKey: 'q22' },
  { category: 'comingSoon', qKey: 'q23' },
];

function AccordionRow({
  question, answer, isOpen, onClick,
}: { question: string; answer: string; isOpen: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-2xl border border-slate-200/70 bg-white px-6 py-5 hover:border-slate-300 transition-colors"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="font-semibold text-base md:text-lg text-slate-900 pr-2">{question}</span>
        <ChevronDown
          className={cn('h-5 w-5 text-slate-400 transition-transform shrink-0', isOpen && 'rotate-180')}
        />
      </div>
      <div className={cn('grid transition-all duration-300', isOpen ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0')}>
        <div className="overflow-hidden">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      </div>
    </button>
  );
}

export default function BestPractices() {
  const { t } = useTranslation();

  // FAQPage structured data — drives Google rich results and answer-engine (GEO) citations.
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_INDEX.map((f) => ({
      '@type': 'Question',
      name: t(`faqsPage.questions.${f.qKey}.q`),
      acceptedAnswer: { '@type': 'Answer', text: t(`faqsPage.questions.${f.qKey}.a`) },
    })),
  };

  // Both /faqs and /best-practices render this; canonicalise both to /faqs.
  usePageMeta('FAQs', FAQ_DESCRIPTION, {
    path: '/faqs',
    jsonLd: faqJsonLd,
    breadcrumbs: [{ name: 'FAQs' }],
  });

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const categories = Array.from(new Set(FAQ_INDEX.map(f => f.category))) as CategoryKey[];
  const norm = (s: string) => s.toLowerCase();
  const q = norm(query.trim());

  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Frequently asked"
          title={
            <>
              The questions athletes —{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">and supporters —</span>{' '}
              ask first.
            </>
          }
          lead={t('faqsPage.hero.subtitle')}
          rail={
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-yellow-400/20 to-blue-500/20 blur-2xl" aria-hidden />
              <label className="relative block">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="search"
                  placeholder="Search the FAQs…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full rounded-2xl bg-white text-slate-900 placeholder:text-slate-400 px-14 py-5 text-base shadow-2xl ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-yellow-400"
                />
              </label>
              <p className="mt-3 text-xs text-slate-400 text-center">Tip: try "fees", "payout", "cancel", "monthly".</p>
            </div>
          }
          size="sm"
        />
      }
    >
      <LightSection>
        <div className="space-y-16">
          {categories.map((cat) => {
            const items = FAQ_INDEX.filter(f => f.category === cat).filter(f => {
              if (!q) return true;
              const question = norm(t(`faqsPage.questions.${f.qKey}.q`));
              const answer = norm(t(`faqsPage.questions.${f.qKey}.a`));
              return question.includes(q) || answer.includes(q);
            });
            if (!items.length) return null;
            const meta = CATEGORY_META[cat];
            const Icon = meta.icon;
            return (
              <div key={cat}>
                <div className="flex items-center gap-3 mb-6">
                  <div className={cn('inline-flex h-10 w-10 items-center justify-center rounded-xl border', meta.tone)}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                    {t(`faqsPage.categories.${cat}`)}
                  </h2>
                </div>
                <div className="space-y-3">
                  {items.map(it => {
                    const key = `${cat}-${it.qKey}`;
                    return (
                      <AccordionRow
                        key={key}
                        question={t(`faqsPage.questions.${it.qKey}.q`)}
                        answer={t(`faqsPage.questions.${it.qKey}.a`)}
                        isOpen={openKey === key}
                        onClick={() => setOpenKey(openKey === key ? null : key)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
          {q &&
            FAQ_INDEX.every(f => {
              const question = norm(t(`faqsPage.questions.${f.qKey}.q`));
              const answer = norm(t(`faqsPage.questions.${f.qKey}.a`));
              return !question.includes(q) && !answer.includes(q);
            }) && (
              <div className="rounded-3xl border border-slate-200/70 bg-slate-50 p-10 text-center">
                <MessageCircle className="h-8 w-8 text-slate-400 mx-auto" />
                <p className="mt-3 text-slate-700">No answers match <span className="font-semibold">"{query}"</span> yet — try our <Link to="/contact" className="text-blue-700 font-semibold">contact page</Link>.</p>
              </div>
            )}
        </div>
        <div className="mt-12 text-center">
          <SectionHead align="center" eyebrow="Don't see it?" title="We answer every email." lead="Most questions get answered the same day. We mean it." />
        </div>
      </LightSection>

      <ClosingBanner
        title={
          <>
            Still have questions? <span className="text-slate-300">We&apos;d love to hear from you.</span>
          </>
        }
        lead={t('faqsPage.cta.subtitle')}
        primary={
          <Link to="/contact">
            <PrimaryCTA>{t('faqsPage.cta.contactButton')} <ArrowRight className="h-5 w-5" /></PrimaryCTA>
          </Link>
        }
        secondary={
          <Link to="/how-it-works">
            <SecondaryCTA>{t('faqsPage.cta.howItWorksButton')}</SecondaryCTA>
          </Link>
        }
      />
    </PageShell>
  );
}
